import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'tw-bg-primary tw-text-text-inverse hover:tw-bg-primary-hover active:tw-bg-primary-active focus-visible:tw-ring-primary',
  secondary:
    'tw-border tw-border-border tw-bg-surface tw-text-text-primary hover:tw-bg-surface-subtle active:tw-bg-surface-disabled focus-visible:tw-ring-secondary',
  ghost:
    'tw-bg-transparent tw-text-text-secondary hover:tw-bg-surface-subtle active:tw-bg-surface-disabled focus-visible:tw-ring-secondary',
};

const sizeClasses: Record<ButtonSize, string> = {
  md: 'tw-min-h-12 tw-px-4 tw-py-3 tw-text-base tw-font-semibold tw-leading-5',
  sm: 'tw-min-h-9 tw-px-3 tw-py-2 tw-text-sm tw-font-medium',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      className = '',
      type = 'button',
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={[
        'tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-rounded-button tw-font-base tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 disabled:tw-opacity-disabled',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? 'tw-w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </button>
  ),
);

Button.displayName = 'Button';
